/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import useAuth from '@/hooks/use-auth';
import { MFAMethod } from '@/types/enums';
import React, { createContext, useContext } from 'react';

interface MFASettings {
    id: string;
    isEnabled: boolean;
    mfaMethod: MFAMethod;
}
export type UserType = {
    id: string;
    name: string;
    email: string;
    isEmailVerified: boolean;
    createdAt: Date;
    updatedAt: Date;
    security: {
        id: string;
        lastPasswordChange: Date;
        recoveryEmail?: string;
        isActive: boolean;
        isDeactivated: boolean;
        mfa: MFASettings[];
    };
};

export type AuthContextType = {
    user: UserType;
    emailFA: MFASettings;
    totpFA: MFASettings;
    hasBackupCodes: boolean;
    error: any;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { data, error, isLoading, isFetching, refetch } = useAuth();
    const user = data?.data?.user;
    const emailFA: MFASettings = user.security.mfa.find(
        (m: MFASettings | undefined): m is MFASettings =>
            m?.mfaMethod === MFAMethod.EMAIL,
    );
    const totpFA: MFASettings = user.security.mfa.find(
        (m: MFASettings | undefined): m is MFASettings =>
            m?.mfaMethod === MFAMethod.TOTP,
    );

    return (
        <AuthContext.Provider
            value={{
                user,
                emailFA,
                totpFA,
                hasBackupCodes: data?.data?.hasBackupcodes,
                error,
                isLoading,
                isFetching,
                refetch,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within a AuthProvider');
    }
    return context;
};
